import React, { useState } from 'react';
import { Table, Button, Space, Input, Modal, Form, Upload, Image, Avatar } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { EditOutlined, PlusOutlined, UploadOutlined, CameraOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { apiService } from '../../../services/api';

const { Search } = Input;
const { TextArea } = Input;

// Backend'den gelen veri için interface
interface AdminCategory {
  id: number;
  name: string;
  definition?: string;
  categoryImage?: string;
  createdAt: string;
  updatedAt?: string;
  mentorCount?: number;
}

const parseCategoriesResponse = (response: any) => {
  const rawData = response?.data ?? response?.Data ?? response;
  const items = Array.isArray(rawData) ? rawData : rawData?.data ?? rawData?.Data ?? [];
  const total = rawData?.totalCount ?? rawData?.TotalCount ?? response?.totalCount ?? response?.TotalCount ?? 0;
  return { items: Array.isArray(items) ? items : [], total };
};

const CategoryListTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<AdminCategory | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading: loading } = useQuery({
    queryKey: adminQueryKeys.categories(currentPage, pageSize),
    queryFn: async () => {
      const r = await apiService.getAdminCategories(currentPage, pageSize);
      const { items, total } = parseCategoriesResponse(r);
      return { items: items as AdminCategory[], total };
    },
    staleTime: 1000 * 60 * 2,
  });
  const categories = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  const createMutation = useMutation({
    mutationFn: (values: any) => apiService.createCategory(values),
    onSuccess: (res) => {
      if (res?.success !== false) {
        setAddModalVisible(false);
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'categories'] });
        toast.success('Kategori başarıyla oluşturuldu');
      } else toast.error('Kategori oluşturulurken hata oluştu');
    },
    onError: () => toast.error('Kategori oluşturulurken hata oluştu'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: any }) => apiService.updateCategory(id, values),
    onSuccess: (res) => {
      if (res?.success !== false) {
        setEditModalVisible(false);
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'categories'] });
        toast.success('Kategori başarıyla güncellendi');
      } else toast.error('Kategori güncellenirken hata oluştu');
    },
    onError: () => toast.error('Kategori güncellenirken hata oluştu'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteCategory(id),
    onSuccess: (res) => {
      if (res?.success !== false) {
        toast.success('Kategori başarıyla silindi');
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'categories'] });
      } else toast.error('Kategori silinirken hata oluştu');
    },
    onError: () => toast.error('Kategori silinirken hata oluştu'),
  });

  const uploadImageMutation = useMutation({
    mutationFn: ({ categoryId, file }: { categoryId: number; file: File }) => apiService.uploadCategoryImage(categoryId, file),
    onSuccess: (res) => {
      if (res?.success !== false && res?.data) {
        toast.success('Resim başarıyla yüklendi');
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'categories'] });
      } else toast.error('Resim yüklenirken hata oluştu');
    },
    onError: () => toast.error('Resim yüklenirken hata oluştu'),
    onSettled: () => setUploading(false),
  });

  const handleAdd = () => {
    setSelectedCategory(null);
    form.resetFields();
    setAddModalVisible(true);
  };

  const handleEdit = (category: AdminCategory) => {
    setSelectedCategory(category);
    form.setFieldsValue({
      ...category
    });
    setEditModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Kategori Sil',
      content: 'Bu kategoriyi silmek istediğinizden emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleCreate = (values: any) => {
    createMutation.mutate(values);
  };

  const handleUpdate = (values: any) => {
    if (selectedCategory?.id) {
      updateMutation.mutate({ id: selectedCategory.id, values });
    }
  };

  const handleImageUpload = (file: File, categoryId: number) => {
    setUploading(true);
    uploadImageMutation.mutate({ categoryId, file });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  const columns = [
    {
      title: 'Resim',
      key: 'image',
      width: 70,
      fixed: 'left' as const,
      render: (record: AdminCategory) => (
        <Avatar
          size={40}
          src={record.categoryImage ? `data:image/jpeg;base64,${record.categoryImage}` : undefined}
          icon={<CameraOutlined />}
          style={{ backgroundColor: 'var(--primary-600)' }}
        />
      )
    },
    {
      title: 'Kategori Adı',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left' as const,
      render: (text: string, record: AdminCategory) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '14px' }}>
            {text}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.mentorCount || 0} mentor
          </div>
        </div>
      )
    },
    {
      title: 'Açıklama',
      dataIndex: 'definition',
      key: 'definition',
      width: 300,
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'Oluşturulma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (record: AdminCategory) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Düzenle"
            size="small"
          />
          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              handleImageUpload(file, record.id);
              return false;
            }}
            accept="image/*"
          >
            <Button 
              type="text" 
              icon={<UploadOutlined />}
              title="Resim Yükle"
              style={{ color: 'var(--success)' }}
              loading={uploading}
              disabled={uploading}
              size="small"
            />
          </Upload>
        </Space>
      )
    }
  ];

  return (
    <div className="panel-tab-content">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 600 }}>Kategori Listesi</h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Search
            placeholder="Kategori ara..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: '100%', maxWidth: '400px' }}
            size="large"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
          >
            Yeni Kategori
          </Button>
        </div>
      </div>

      <TabListLoading loading={loading}>
      <div className="panel-table-card">
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          size="middle"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kategori`,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            pageSizeOptions: ['10', '20', '50', '100'],
            style: { marginTop: '16px' }
          }}
        />
      </div>
      </TabListLoading>

      {/* Kategori Ekleme Modal */}
      <Modal
        title="Yeni Kategori Ekle"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="name"
            label="Kategori Adı"
            rules={[{ required: true, message: 'Kategori adı gereklidir' }]}
          >
            <Input placeholder="Kategori adını girin" />
          </Form.Item>
          
          <Form.Item
            name="definition"
            label="Açıklama"
          >
            <TextArea 
              rows={4} 
              placeholder="Kategori açıklamasını girin"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Kategori Düzenleme Modal */}
      <Modal
        title="Kategori Düzenle"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            name="name"
            label="Kategori Adı"
            rules={[{ required: true, message: 'Kategori adı gereklidir' }]}
          >
            <Input placeholder="Kategori adını girin" />
          </Form.Item>
          
          <Form.Item
            name="definition"
            label="Açıklama"
          >
            <TextArea 
              rows={4} 
              placeholder="Kategori açıklamasını girin"
            />
          </Form.Item>
          
          <Form.Item label="Mevcut Resim">
            {selectedCategory?.categoryImage ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Image
                  width={100}
                  height={100}
                  src={`data:image/jpeg;base64,${selectedCategory.categoryImage}`}
                  style={{ borderRadius: '8px', objectFit: 'cover' }}
                />
                <div style={{ color: '#666', fontSize: '12px' }}>
                  Mevcut resim
                </div>
              </div>
            ) : (
              <div style={{ 
                width: 100, 
                height: 100, 
                border: '2px dashed #d9d9d9', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999'
              }}>
                Resim yok
              </div>
            )}
          </Form.Item>
          
          <Form.Item label="Resim Yükle">
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                if (selectedCategory?.id) {
                  handleImageUpload(file, selectedCategory.id);
                }
                return false;
              }}
              accept="image/*"
            >
              <Button 
                icon={<UploadOutlined />}
                loading={uploading}
                disabled={uploading}
              >
                {uploading ? 'Yükleniyor...' : 'Resim Seç'}
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryListTab;